/* eslint-disable @typescript-eslint/naming-convention */
import axios, { CancelTokenSource } from 'axios';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { injectable } from '@servicetitan/react-ioc';

import {
    AgentLaunchContext,
    ChatApi,
    SendAgentQuestionRequestModel,
} from '../../common/api/chat.api';
import { TechniciansApi } from '../../common/api/technicians.api';
import { MessageType } from '../../common/models/message-type.model';
import { ChatStore } from '../../common/stores/chat.store';
import { getBaseRequestParams } from '../../common/utils/base-request-params';
import { AGENTS, isKnownAgent } from '../agents';

export type AgentChatStatus = 'loading' | 'ready' | 'unknown-agent' | 'locked';

const FALLBACK_TEXT = `Sorry, I couldn't answer your question. Please try rephrasing or asking a different question, and I'll do my best to help.`;

/*
 * Opening suggested prompts per launch context (mid-call shows the report instead, no prompts).
 * Hard-coded the same way question-picker's predefinedQuestions are.
 */
const GENERAL_PROMPTS = [
    'Tips for connecting with the homeowner',
    'Role-play a price objection',
    'How do I transition from diagnosis to options?',
];
const RECORDING_PROMPTS = ['What did I do well on this job?', 'What could I have done better?'];

const REPORT_READY_TEXT = 'Your Mid-call check-in is ready!';
const REPORT_NOT_READY_TEXT =
    "Your Mid-call check-in isn't ready yet. Check back once you're further into the call.";
const REGENERATE_BUTTON_TEXT = 'Regenerate Mid-call check-in';
const REGENERATING_TEXT = 'Regenerating your Mid-call check-in. This can take a moment.';
const REGENERATE_TIMEOUT_TEXT = 'Still regenerating — please try again in a moment.';
const PROMPTS_INTRO_TEXT = 'How can I help you today? Here are some things you can ask:';

/*
 * Regeneration is asynchronous on salespro-web: the request returns 202 and the refreshed content is
 * produced later. We poll the report and watch its version, swapping in the new one once it increments.
 */
const REGENERATE_POLL_INTERVAL_MS = 3000;
const REGENERATE_POLL_MAX_ATTEMPTS = 10;

/*
 * On open, a report older than this is considered stale and is regenerated before being shown,
 * so the technician always sees a check-in reflecting the latest call state.
 */
const REPORT_STALE_THRESHOLD_MS = 2 * 60 * 1000;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const entryToLaunchContext = (entry: string): AgentLaunchContext => {
    switch (entry) {
        case 'mid-call':
            return 'mid-call';
        case 'recording':
            return 'recording';
        default:
            return 'general';
    }
};

@injectable()
export class AgentChatStore {
    @observable status: AgentChatStatus = 'loading';
    @observable loading = false;
    /*
     * True while a mid-call check-in regeneration is in flight. Shows the "Regenerating…" message
     * plus the three-dot loader and disables the Regenerate button. Kept separate from `loading`
     * so the footer send state isn't blocked by report refreshes.
     */
    @observable regenerating = false;
    // Demo tenants only, and only for an appointment this technician recorded.
    @observable demoMcciEnabled = false;
    @observable generatingDemo = false;

    private endpoint?: string;
    private entry = '';
    private entryId = '';
    private currentReportVersion = 0;
    /*
     * Markdown of the report currently on screen — used to re-render it when a manual
     * regeneration times out and the "Regenerating…" notice has to be swapped away.
     */
    private currentReportMarkdown?: string;
    /*
     * Bumped on every init() and dispose() so a stale (superseded) async init — e.g. StrictMode's
     * init -> dispose -> init double-invoke — skips enqueuing its opening content.
     */
    private initGeneration = 0;
    /*
     * When a deep-link `prompt` is provided, skip the opening suggested-prompt chips —
     * the page auto-sends the prompt instead.
     */
    private skipOpeningPrompts = false;
    private cancelToken?: CancelTokenSource;
    private pollCancelToken?: CancelTokenSource;

    constructor(
        private readonly chatStore: ChatStore,
        private readonly chatApi: ChatApi,
        private readonly techniciansApi: TechniciansApi
    ) {
        makeObservable(this);
    }

    @action
    init = async (agentName: string, entry: string, entryId?: string, autoPrompt?: string) => {
        this.chatStore.resetMessaging();
        const generation = ++this.initGeneration;
        this.status = 'loading';
        this.entry = entry;
        this.entryId = entryId ?? '';
        this.skipOpeningPrompts = !!autoPrompt?.trim();
        this.currentReportVersion = 0;
        this.currentReportMarkdown = undefined;
        this.regenerating = false;

        if (!isKnownAgent(agentName)) {
            runInAction(() => {
                this.status = 'unknown-agent';
            });
            return;
        }
        this.endpoint = AGENTS[agentName].endpoint;

        /*
         * Gate on the Field Pro Coaching entitlement (FE -> BFF -> salespro-web microservice).
         * If the entitlement check fails, fail closed (locked) rather than exposing the agent.
         */
        let isEnabled = false;
        try {
            const { data } = await this.techniciansApi.getFieldProTopicAvailability();
            isEnabled = data.isEnabled;
        } catch {
            isEnabled = false;
        }

        // Bail if a newer init() (or dispose()) superseded this run while awaiting.
        if (generation !== this.initGeneration) {
            return;
        }

        runInAction(() => {
            this.status = isEnabled ? 'ready' : 'locked';
        });

        if (isEnabled) {
            /*
             * Deliberately not awaited, and after the entitlement gate: a slow or broken demo probe
             * must not delay the check-in. The button appears if and when the probe lands.
             */
            this.probeDemoMcciAvailability(generation).catch(() => undefined);
            await this.loadOpeningContent(generation);
        }
    };

    @action
    regenerate = async () => {
        /*
         * The in-chat Regenerate button is a chat message and cannot be disabled, so a demo
         * generation in flight has to be rejected here or both would write the same report.
         */
        if (!this.entryId || this.regenerating || this.generatingDemo) {
            return;
        }
        const appointmentId = Number(this.entryId);
        if (Number.isNaN(appointmentId)) {
            return;
        }

        this.regenerating = true;
        try {
            // No fallback: the current report is already on screen, so a timeout just leaves it there.
            await this.regenerateAndPoll(appointmentId);
        } finally {
            runInAction(() => {
                this.regenerating = false;
            });
        }
    };

    // Synchronous, unlike `regenerate`: salespro-web returns the written report, so nothing polls.
    @action
    generateDemo = async () => {
        if (!this.entryId || this.generatingDemo || this.regenerating) {
            return;
        }
        const appointmentId = Number(this.entryId);
        if (Number.isNaN(appointmentId)) {
            return;
        }

        const generation = this.initGeneration;
        this.generatingDemo = true;
        try {
            const { data } = await this.techniciansApi.generateDemoMidCallReport(appointmentId);
            if (!this.isCurrentInit(generation)) {
                return;
            }

            // 204 = no fixture configured. Unavailable is not an error state, so just retire it.
            if (!data || !data.markdownContent) {
                runInAction(() => {
                    this.demoMcciEnabled = false;
                });
                return;
            }

            // Swap the not-ready notice (or a previous report) rather than stacking a second one.
            this.chatStore.filterOutRemovableMessages();
            runInAction(() => {
                this.currentReportVersion = data.version;
            });
            this.enqueueReport(data.markdownContent);
        } catch {
            // Deliberately silent: a failed demo is not the technician's problem. Button stays.
        } finally {
            if (this.isCurrentInit(generation)) {
                runInAction(() => {
                    this.generatingDemo = false;
                });
            }
        }
    };

    handleMessage = async (message: string) => {
        if (!message.trim() || this.status !== 'ready' || !this.endpoint) {
            return;
        }

        this.chatStore.enqueueMessages([
            {
                isMine: true,
                showAvatar: true,
                type: MessageType.Text,
                text: message,
                date: new Date(),
            },
        ]);

        await this.sendMessage(message);
    };

    dispose = () => {
        // Invalidate any in-flight init() so its post-await continuation won't re-enqueue.
        this.initGeneration++;
        this.cancelToken?.cancel();
        this.pollCancelToken?.cancel();
        this.chatStore.resetMessaging();
        runInAction(() => {
            this.regenerating = false;
            this.generatingDemo = false;
            this.demoMcciEnabled = false;
        });
    };

    // True while the given init() run is still the current one (not superseded by a newer init/dispose).
    private isCurrentInit = (generation: number) => generation === this.initGeneration;

    /*
     * Mid-call only: other launch contexts have no appointment for a demo to attach to. Never
     * throws and never touches anything but `demoMcciEnabled`, so the worst case is a button that
     * stays hidden.
     */
    private probeDemoMcciAvailability = async (generation: number): Promise<void> => {
        const appointmentId = Number(this.entryId);
        if (
            entryToLaunchContext(this.entry) !== 'mid-call' ||
            !this.entryId ||
            Number.isNaN(appointmentId)
        ) {
            return;
        }

        try {
            const { data } = await this.techniciansApi.getDemoMcciAvailability(appointmentId);
            if (!this.isCurrentInit(generation)) {
                return;
            }
            runInAction(() => {
                this.demoMcciEnabled = Boolean(data?.enabled);
            });
        } catch {
            // Leave the button hidden.
        }
    };

    /*
     * Opening content depends on the launch context: mid-call shows the halftime report (no prompts),
     * the other contexts show suggested prompts.
     */
    private loadOpeningContent = async (generation: number) => {
        switch (entryToLaunchContext(this.entry)) {
            case 'mid-call':
                await this.loadMidCallReport(generation);
                break;
            case 'recording':
                if (!this.skipOpeningPrompts) {
                    this.enqueueSuggestedPrompts(RECORDING_PROMPTS, generation);
                }
                break;
            default:
                if (!this.skipOpeningPrompts) {
                    this.enqueueSuggestedPrompts(GENERAL_PROMPTS, generation);
                }
        }
    };

    @action
    private loadMidCallReport = async (generation: number) => {
        if (!this.entryId) {
            this.enqueueNotReadyNotice(generation);
            return;
        }
        const appointmentId = Number(this.entryId);
        if (Number.isNaN(appointmentId)) {
            this.enqueueNotReadyNotice(generation);
            return;
        }

        this.loading = true;
        try {
            const { data } = await this.techniciansApi.getMidCallReport(appointmentId);
            if (!this.isCurrentInit(generation)) {
                return;
            }
            // 204 No Content (report not generated yet) resolves with an empty string body.
            if (!data || !data.markdownContent) {
                this.enqueueNotReadyNotice(generation);
                return;
            }
            runInAction(() => {
                this.currentReportVersion = data.version;
            });

            if (this.isReportStale(data.updatedAt)) {
                /*
                 * Stale on open: regenerate before showing anything. We hold the stale markdown as a
                 * fallback (shown if regeneration times out) so the technician is never left empty.
                 * Hand the wait off to `regenerating` so the "Regenerating…" message — not the
                 * agent-response spinner — is what's visible while polling.
                 */
                runInAction(() => {
                    this.loading = false;
                    this.regenerating = true;
                });
                try {
                    await this.regenerateAndPoll(appointmentId, data.markdownContent);
                } finally {
                    runInAction(() => {
                        this.regenerating = false;
                    });
                }
                return;
            }

            this.enqueueReport(data.markdownContent);
        } catch {
            this.enqueueNotReadyNotice(generation);
        } finally {
            // A superseded run must not clear the flag the newer init's load owns.
            if (this.isCurrentInit(generation)) {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    };

    // A report is stale if it was generated more than the threshold ago; missing/unparsable -> not stale.
    private isReportStale = (updatedAt?: string): boolean => {
        if (!updatedAt) {
            return false;
        }
        const updatedMs = Date.parse(updatedAt);
        return !Number.isNaN(updatedMs) && Date.now() - updatedMs > REPORT_STALE_THRESHOLD_MS;
    };

    /*
     * Shared regeneration flow for both the manual button and the stale-on-open path. Requests an
     * (async) regeneration, then polls until a newer version appears. `fallbackMarkdown` is provided
     * only by the stale-on-open path: it's the stale report to fall back to if regeneration can't be
     * started or never completes. Loading/regenerating flags are owned by the callers.
     */
    private regenerateAndPoll = async (appointmentId: number, fallbackMarkdown?: string) => {
        /*
         * Guard every post-await continuation on the generation: dispose() (or a newer init())
         * bumps it, and an unguarded continuation here would enqueue into — or reset — the
         * superseding page's chat. The pollCancelToken alone can't cover the window before the
         * poll creates it.
         */
        const generation = this.initGeneration;
        try {
            await this.techniciansApi.regenerateMidCallReport(appointmentId);
        } catch {
            if (!this.isCurrentInit(generation)) {
                return;
            }
            if (fallbackMarkdown) {
                this.enqueueReport(fallbackMarkdown);
            } else {
                this.enqueueText(REGENERATE_TIMEOUT_TEXT);
            }
            return;
        }

        if (!this.isCurrentInit(generation)) {
            return;
        }
        this.enqueueText(REGENERATING_TEXT, true);
        await this.pollForRegeneratedReport(appointmentId, generation, fallbackMarkdown);
    };

    /*
     * Polls the report until its version exceeds the one we last displayed, then swaps in the fresh
     * report. Gives up after a bounded number of attempts so the spinner can't hang indefinitely.
     */
    private pollForRegeneratedReport = async (
        appointmentId: number,
        generation: number,
        fallbackMarkdown?: string
    ) => {
        this.pollCancelToken?.cancel();
        this.pollCancelToken = axios.CancelToken.source();
        const { token } = this.pollCancelToken;

        // Sequential by design: wait, then poll once, repeat — so awaiting inside the loop is intended.
        /* eslint-disable no-await-in-loop */
        for (let attempt = 0; attempt < REGENERATE_POLL_MAX_ATTEMPTS; attempt++) {
            await wait(REGENERATE_POLL_INTERVAL_MS);
            if (token.reason || !this.isCurrentInit(generation)) {
                // Cancelled or superseded (e.g. the page was disposed).
                return;
            }

            try {
                const { data } = await this.techniciansApi.getMidCallReport(appointmentId, token);
                if (!this.isCurrentInit(generation)) {
                    return;
                }
                if (data && data.markdownContent && data.version > this.currentReportVersion) {
                    // Swap only the old report block + "Regenerating…" notice; keep the Q&A history.
                    this.chatStore.filterOutRemovableMessages();
                    runInAction(() => {
                        this.currentReportVersion = data.version;
                    });
                    this.enqueueReport(data.markdownContent);
                    return;
                }
            } catch (err) {
                if (axios.isCancel(err)) {
                    return;
                }
                // Transient error — keep polling until attempts are exhausted.
            }
        }
        /* eslint-enable no-await-in-loop */

        // Timed out waiting for the new version.
        if (!this.isCurrentInit(generation)) {
            return;
        }
        if (fallbackMarkdown) {
            // Stale-on-open: swap the "Regenerating…" notice for the stale report.
            this.chatStore.filterOutRemovableMessages();
            this.enqueueReport(fallbackMarkdown);
        } else if (this.currentReportMarkdown) {
            /*
             * Manual regenerate: the "Regenerating…" notice can't be removed alone (the filter
             * also takes the report block), so swap both out and re-render the current report
             * with the timeout note below it.
             */
            this.chatStore.filterOutRemovableMessages();
            this.enqueueReport(this.currentReportMarkdown);
            this.enqueueText(REGENERATE_TIMEOUT_TEXT);
        } else {
            // No report on screen to restore — just note the timeout.
            this.enqueueText(REGENERATE_TIMEOUT_TEXT);
        }
    };

    /*
     * The report block is marked `removable` so a later regeneration can swap it (and the
     * "Regenerating…" notice) out via filterOutRemovableMessages() without touching the
     * technician's Q&A history.
     */
    private enqueueReport = (markdown: string) => {
        this.currentReportMarkdown = markdown;
        this.chatStore.enqueueMessages([
            {
                isMine: false,
                showAvatar: true,
                type: MessageType.RichText,
                text: REPORT_READY_TEXT,
                removable: true,
            },
            {
                isMine: false,
                showAvatar: false,
                type: MessageType.MidCallReport,
                data: markdown,
                removable: true,
            },
            {
                isMine: false,
                showAvatar: false,
                type: MessageType.Button,
                text: REGENERATE_BUTTON_TEXT,
                onClick: this.regenerate,
                removable: true,
            },
        ]);
    };

    private enqueueNotReadyNotice = (generation: number) => {
        if (!this.isCurrentInit(generation)) {
            return;
        }
        /*
         * Removable so Generate Demo replaces it rather than stacking the report underneath. Safe
         * for the other clearers: regenerate is only reachable once a report exists, and then this
         * notice was never shown.
         */
        this.enqueueText(REPORT_NOT_READY_TEXT, true);
    };

    private enqueueText = (text: string, removable = false) => {
        this.chatStore.enqueueMessages([
            {
                isMine: false,
                showAvatar: true,
                type: MessageType.Text,
                text,
                removable,
            },
        ]);
    };

    private enqueueSuggestedPrompts = (prompts: string[], generation: number) => {
        if (!this.isCurrentInit(generation)) {
            return;
        }
        this.chatStore.enqueueMessages([
            {
                isMine: false,
                showAvatar: true,
                type: MessageType.RichText,
                text: PROMPTS_INTRO_TEXT,
            },
            {
                isMine: false,
                showAvatar: false,
                type: MessageType.Question,
                data: prompts,
            },
        ]);
    };

    @action
    private sendMessage = async (message: string) => {
        this.loading = true;
        this.cancelToken?.cancel();
        const cancelSource = axios.CancelToken.source();
        this.cancelToken = cancelSource;
        /*
         * Guard post-await continuations: a superseded send (or dispose) must neither enqueue
         * into the next screen's chat nor clear the newer request's loading flag.
         */
        const generation = this.initGeneration;

        try {
            const requestParams: SendAgentQuestionRequestModel = {
                id: window.crypto.randomUUID(),
                message: {
                    role: 'user',
                    content: message,
                    timestamp: new Date(),
                },
                launch_context: entryToLaunchContext(this.entry),
                ...this.entryContext(),
                ...getBaseRequestParams(),
            };

            const { data } = await this.chatApi.sendAgentQuestion(
                this.endpoint!,
                requestParams,
                cancelSource.token
            );

            if (!this.isCurrentInit(generation)) {
                return;
            }
            const { content, entity_type } = data.response;

            this.chatStore.enqueueMessages([
                {
                    showAvatar: true,
                    isMine: false,
                    data: content.entity_data,
                    type: entity_type,
                },
            ]);
        } catch (err) {
            if (axios.isCancel(err) || !this.isCurrentInit(generation)) {
                return;
            }
            this.chatStore.enqueueMessages([
                {
                    isMine: false,
                    showAvatar: true,
                    type: MessageType.Text,
                    text: FALLBACK_TEXT,
                },
            ]);
        } finally {
            /*
             * Only the still-active request may clear the flag — a superseded send finishing
             * late must not hide the loader for the request that replaced it.
             */
            if (this.cancelToken === cancelSource) {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    };

    // mid-call / job entries are keyed by appointment_id; recording entries by recording_id.
    private entryContext = (): Pick<
        SendAgentQuestionRequestModel,
        'appointment_id' | 'recording_id'
    > => {
        if (this.entry === 'recording') {
            return this.entryId ? { recording_id: this.entryId } : {};
        }
        if (!this.entryId) {
            return {};
        }
        const appointmentId = Number(this.entryId);
        return Number.isNaN(appointmentId) ? {} : { appointment_id: appointmentId };
    };
}
